import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';

interface ConditionClause {
  field: string;
  operator: string;
  value: string;
  logicalOperator?: string | null; // AND/OR operator before this clause (null for first clause)
}

// Mapping of internal enum values to user-friendly display labels
const VALUE_TO_LABEL_MAP: Record<string, string> = {
  // Impact values (CaseImpact enum)
  'high_1': 'High',
  'medium_2': 'Medium',
  'low_3': 'Low',

  // Priority values (CasePriority enum - 5 values)
  'critical_1': 'Critical',
  'high_2': 'High',
  'moderate_3': 'Moderate',
  'low_4': 'Low',
  'planning_5': 'Planning',

  // Urgency values (CaseUrgency enum)
  // Same as Impact values

  // Generic state values
  'NEW': 'New',
  'OPEN': 'Open',
  'IN_PROGRESS': 'In Progress',
  'ON_HOLD': 'On Hold',
  'RESOLVED': 'Resolved',
  'CLOSED': 'Closed',
  'CANCELLED': 'Cancelled',
  'INFORMATION_REQUIRED': 'Information Required',
  'APPROVED': 'Approved',
  'ASSESSMENT': 'Assessment',
  'SCHEDULED': 'Scheduled',
  'IMPLEMENT': 'Implement',
  'REVIEW': 'Review',

  // Activity type values for LAST_ACTIVITY_TYPE field
  'NOTE_ADDED_CUSTOMER': 'Customer Note Added',
  'NOTE_ADDED_INTERNAL': 'Internal Note Added',
  'state_change': 'State Change',
  'created': 'Created',
  'assigned': 'Assigned',
  'attachment_added': 'Attachment Added',
  'sla_breach': 'SLA Breach',
  'sla_warning': 'SLA Warning'
};

// Helper function to convert enum values to user-friendly labels
const formatEnumLabel = (value: string): string => {
  if (!value) return value;

  // Check if we have a predefined mapping
  if (VALUE_TO_LABEL_MAP[value]) {
    return VALUE_TO_LABEL_MAP[value];
  }

  // Fallback: Convert UPPER_SNAKE_CASE to Title Case
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const conditionTypeOptions = [
  { value: 'START', label: 'Start' },
  { value: 'PAUSE', label: 'Pause' },
  { value: 'RESUME', label: 'Resume' },
  { value: 'RESET', label: 'Reset' },
  { value: 'CANCEL', label: 'Cancel' },
  { value: 'END', label: 'End' }
];

// Generic field options - applies to all entity types
const fieldOptions: Array<{ value: string; label: string }> = [
  { value: 'PRIORITY', label: 'Priority' },
  { value: 'IMPACT', label: 'Impact' },
  { value: 'STATE', label: 'State' },
  { value: 'ASSIGNMENT_GROUP', label: 'Assignment Group' },
  { value: 'LAST_ACTIVITY_TYPE', label: 'Last Activity Type' }
];

const operatorOptions = [
  { value: 'EQUALS', label: 'Equals' },
  { value: 'NOT_EQUALS', label: 'Not Equals' },
  { value: 'IN', label: 'In' },
  { value: 'NOT_IN', label: 'Not In' },
  { value: 'IS_EMPTY', label: 'Is Empty' },
  { value: 'IS_NOT_EMPTY', label: 'Is Not Empty' }
];

const SlaConditionForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    conditionType: 'START',
    isActive: true
  });

  const [clauses, setClauses] = useState<ConditionClause[]>([
    { field: 'PRIORITY', operator: 'EQUALS', value: '', logicalOperator: null }
  ]);

  const [fieldEnums, setFieldEnums] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<Array<{ id: string; name: string }>>([]);

  const fetchFieldEnums = useCallback(async (field: string) => {
    if (field === 'ASSIGNMENT_GROUP' || field === 'ASSIGNED_TO_GROUP') {
      return;
    }

    try {
      // For generic conditions, don't pass entityType - will get generic states for STATE field
      const response = await api.get(`/sla-conditions/enums/${field}`);
      setFieldEnums((prev) => ({
        ...prev,
        [field]: response.data.values || []
      }));
    } catch (err) {
      console.error('Error fetching field enums:', err);
    }
  }, []);

  const fetchCondition = useCallback(async (conditionId: string) => {
    try {
      setLoading(true);
      const response = await api.get(`/sla-conditions/${conditionId}`);
      const condition = response.data;

      setFormData({
        name: condition.name,
        description: condition.description || '',
        conditionType: condition.conditionType,
        isActive: condition.isActive
      });

      if (condition.SlaConditionClause && condition.SlaConditionClause.length > 0) {
        const loadedClauses = condition.SlaConditionClause.map((clause: any) => {
          let value = clause.value || '';

          // Convert old Priority format to new format for backward compatibility
          if (clause.field === 'PRIORITY' && value) {
            const oldToNewMap: Record<string, string> = {
              'CRITICAL': 'critical_1',
              'HIGH': 'high_2',
              'NORMAL': 'moderate_3',
              'MEDIUM': 'moderate_3',
              'MODERATE': 'moderate_3',
              'LOW': 'low_4'
            };
            const upperValue = value.toUpperCase();
            if (oldToNewMap[upperValue]) {
              value = oldToNewMap[upperValue];
            }
          }

          return {
            field: clause.field,
            operator: clause.operator,
            value,
            logicalOperator: clause.logicalOperator || null
          };
        });

        // Fetch enums for all fields in the loaded clauses BEFORE setting clauses
        const uniqueFields = Array.from(new Set(
          loadedClauses
            .map((c: ConditionClause) => c.field)
            .filter((f: string) => f !== 'ASSIGNMENT_GROUP' && f !== 'ASSIGNED_TO_GROUP')
        )) as string[];

        // Fetch all field enums in parallel
        await Promise.all(uniqueFields.map(field => fetchFieldEnums(field)));

        // Set clauses after enums are loaded
        setClauses(loadedClauses);
      }
    } catch (err: any) {
      console.error('Error fetching condition:', err);
      setError('Failed to load condition');
    } finally {
      setLoading(false);
    }
  }, [fetchFieldEnums]);

  const fetchGroups = useCallback(async () => {
    try {
      const response = await api.get('/groups', { params: { limit: 1000 } });
      setGroups(response.data.groups || []);
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  }, []);

  useEffect(() => {
    if (isEdit && id) {
      fetchCondition(id);
    }
    fetchGroups();
  }, [id, isEdit, fetchCondition, fetchGroups]);

  // Fetch enums for initial clauses
  useEffect(() => {
    clauses.forEach((clause) => {
      if (clause.field && clause.field !== 'ASSIGNMENT_GROUP' && clause.field !== 'ASSIGNED_TO_GROUP') {
        fetchFieldEnums(clause.field);
      }
    });
  }, [clauses, fetchFieldEnums]);

  const handleClauseChange = (index: number, field: keyof ConditionClause, value: string) => {
    const updatedClauses = [...clauses];
    updatedClauses[index][field] = value;

    // Reset value if operator changes to IS_EMPTY or IS_NOT_EMPTY
    if (field === 'operator' && (value === 'IS_EMPTY' || value === 'IS_NOT_EMPTY')) {
      updatedClauses[index].value = '';
    }

    // Fetch enums when field changes
    if (field === 'field') {
      updatedClauses[index].value = '';
      fetchFieldEnums(value);
    }

    setClauses(updatedClauses);
  };

  const addClause = () => {
    // New clauses should default to AND operator (except the first clause which has null)
    const logicalOperator = clauses.length === 0 ? null : 'AND';
    setClauses([...clauses, { field: 'PRIORITY', operator: 'EQUALS', value: '', logicalOperator }]);
  };

  const removeClause = (index: number) => {
    if (clauses.length > 1) {
      const updatedClauses = clauses.filter((_, i) => i !== index);
      // Ensure the first clause always has null logical operator
      if (updatedClauses.length > 0 && updatedClauses[0].logicalOperator !== null) {
        updatedClauses[0] = { ...updatedClauses[0], logicalOperator: null };
      }
      setClauses(updatedClauses);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate clauses
    for (const clause of clauses) {
      if (!clause.field || !clause.operator) {
        setError('All clauses must have a field and operator');
        return;
      }

      if (
        clause.operator !== 'IS_EMPTY' &&
        clause.operator !== 'IS_NOT_EMPTY' &&
        !clause.value
      ) {
        setError(`Clause with operator ${clause.operator} requires a value`);
        return;
      }
    }

    try {
      setLoading(true);

      const payload = {
        ...formData,
        clauses: clauses.map((clause) => ({
          field: clause.field,
          operator: clause.operator,
          value: clause.operator === 'IN' || clause.operator === 'NOT_IN'
            ? JSON.stringify(Array.isArray(clause.value) ? clause.value : clause.value.split(',').map(v => v.trim()))
            : clause.value,
          logicalOperator: clause.logicalOperator
        }))
      };

      if (isEdit && id) {
        await api.put(`/sla-conditions/${id}`, payload);
      } else {
        await api.post('/sla-conditions', payload);
      }

      navigate('/admin/sla-conditions');
    } catch (err: any) {
      console.error('Error saving condition:', err);
      setError(err.response?.data?.error || 'Failed to save condition');
    } finally {
      setLoading(false);
    }
  };

  const renderValueInput = (clause: ConditionClause, index: number) => {
    if (clause.operator === 'IS_EMPTY' || clause.operator === 'IS_NOT_EMPTY') {
      return null;
    }

    if (clause.field === 'ASSIGNMENT_GROUP' || clause.field === 'ASSIGNED_TO_GROUP') {
      if (clause.operator === 'IN' || clause.operator === 'NOT_IN') {
        return (
          <select
            multiple
            value={clause.value ? clause.value.split(',') : []}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, option => option.value);
              handleClauseChange(index, 'value', selected.join(','));
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            size={5}
          >
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        );
      } else {
        return (
          <select
            value={clause.value}
            onChange={(e) => handleClauseChange(index, 'value', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select Group</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        );
      }
    }

    const enumValues = fieldEnums[clause.field] || [];

    if (clause.operator === 'IN' || clause.operator === 'NOT_IN') {
      if (enumValues.length > 0) {
        return (
          <select
            multiple
            value={clause.value ? clause.value.split(',') : []}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, option => option.value);
              handleClauseChange(index, 'value', selected.join(','));
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            size={5}
          >
            {enumValues.map((val) => (
              <option key={val} value={val}>
                {formatEnumLabel(val)}
              </option>
            ))}
          </select>
        );
      } else {
        return (
          <input
            type="text"
            value={clause.value}
            onChange={(e) => handleClauseChange(index, 'value', e.target.value)}
            placeholder="Comma-separated values"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        );
      }
    }

    if (enumValues.length > 0) {
      return (
        <select
          value={clause.value}
          onChange={(e) => handleClauseChange(index, 'value', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select Value</option>
          {enumValues.map((val) => (
            <option key={val} value={val}>
              {formatEnumLabel(val)}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type="text"
        value={clause.value}
        onChange={(e) => handleClauseChange(index, 'value', e.target.value)}
        placeholder="Enter value"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    );
  };

  if (loading && isEdit) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Loading condition...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-full">
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/sla-conditions')}
          className="text-blue-600 hover:text-blue-800 mb-4"
        >
          ← Back to Conditions
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEdit ? 'Edit SLA Condition' : 'Create SLA Condition'}
        </h1>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left side: Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Basic Information</h2>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Condition Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., High Priority Case Started"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Condition Type *
                </label>
                <select
                  required
                  value={formData.conditionType}
                  onChange={(e) => setFormData({ ...formData, conditionType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {conditionTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Describe what this condition checks for..."
              />
            </div>

            <div>
              <p className="text-sm text-gray-600">
                Note: Conditions are now generic and reusable across all entity types.
                The entity type will be specified when creating an SLA policy.
              </p>
            </div>

            <div className="flex items-center">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Active</span>
              </label>
            </div>
          </div>
        </div>

        {/* Condition Clauses */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Condition Clauses</h2>
            <button
              type="button"
              onClick={addClause}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Add Clause
            </button>
          </div>

          <div className="space-y-4">
            {clauses.map((clause, index) => (
              <div key={index}>
                {/* Logical Operator - only show for clauses after the first */}
                {index > 0 && (
                  <div className="flex justify-center my-2">
                    <select
                      value={clause.logicalOperator || 'AND'}
                      onChange={(e) => handleClauseChange(index, 'logicalOperator', e.target.value)}
                      className="px-4 py-2 bg-blue-50 border-2 border-blue-300 rounded-lg font-semibold text-blue-700 focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="AND">AND</option>
                      <option value="OR">OR</option>
                    </select>
                  </div>
                )}

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-sm font-medium text-gray-600">
                      Clause {index + 1}
                    </span>
                    {clauses.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeClause(index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Field *
                    </label>
                    <select
                      value={clause.field}
                      onChange={(e) => handleClauseChange(index, 'field', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {fieldOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Operator *
                    </label>
                    <select
                      value={clause.operator}
                      onChange={(e) => handleClauseChange(index, 'operator', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {operatorOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Value {clause.operator !== 'IS_EMPTY' && clause.operator !== 'IS_NOT_EMPTY' && '*'}
                    </label>
                    {renderValueInput(clause, index)}
                  </div>
                </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> Use AND/OR operators between clauses to create complex conditions.
              Select the logical operator before each clause (except the first) to define how they combine.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/admin/sla-conditions')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {loading ? 'Saving...' : isEdit ? 'Update Condition' : 'Create Condition'}
          </button>
        </div>
      </form>
        </div>

        {/* Right side: Information Panel */}
        <div className="lg:col-span-3">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 sticky top-8">
            <h3 className="text-base font-bold text-blue-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Generic State Mappings
            </h3>

            <div className="text-xs text-blue-900 mb-3">
              <p className="font-medium mb-1">How Generic States Work:</p>
              <p className="text-blue-800">
                SLA conditions use generic states that automatically map to entity-specific states when applied.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              {/* Column 1 */}
              <div className="space-y-3">
                {/* Case States */}
                <div className="bg-white rounded-lg p-3 border border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-2 text-xs">Case</h4>
                  <div className="space-y-1 text-xs">
                    <div><span className="font-medium text-blue-700">New</span> → New</div>
                    <div><span className="font-medium text-blue-700">Open</span> → Open</div>
                    <div><span className="font-medium text-blue-700">Information Required</span> → Awaiting Info, Solution Proposed, Pending Approval</div>
                    <div><span className="font-medium text-blue-700">On Hold</span> → On Hold</div>
                    <div><span className="font-medium text-blue-700">Closed</span> → Closed, Closed Complete, Closed Incomplete</div>
                    <div><span className="font-medium text-blue-700">Resolved</span> → Auto Resolved</div>
                    <div><span className="font-medium text-blue-700">In Progress</span> → In Progress</div>
                    <div><span className="font-medium text-blue-700">Approved</span> → Approved</div>
                    <div><span className="font-medium text-blue-700">Skipped</span> → Skipped</div>
                    <div><span className="font-medium text-blue-700">Cancelled</span> → Cancelled</div>
                  </div>
                </div>

                {/* Change States */}
                <div className="bg-white rounded-lg p-3 border border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-2 text-xs">Change Request</h4>
                  <div className="space-y-1 text-xs">
                    <div><span className="font-medium text-blue-700">New</span> → New</div>
                    <div><span className="font-medium text-blue-700">Assessment</span> → Assess</div>
                    <div><span className="font-medium text-blue-700">Approved</span> → Authorize</div>
                    <div><span className="font-medium text-blue-700">Scheduled</span> → Scheduled</div>
                    <div><span className="font-medium text-blue-700">Implement</span> → Implement</div>
                    <div><span className="font-medium text-blue-700">Review</span> → Review</div>
                    <div><span className="font-medium text-blue-700">Closed</span> → Closed</div>
                    <div><span className="font-medium text-blue-700">Cancelled</span> → Cancelled</div>
                  </div>
                </div>
              </div>

              {/* Column 2 */}
              <div className="space-y-3">
                {/* Incident States */}
                <div className="bg-white rounded-lg p-3 border border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-1.5 text-xs">Incident</h4>
                  <div className="space-y-0.5 text-xs">
                    <div><span className="font-medium text-blue-700">New</span> → New</div>
                    <div><span className="font-medium text-blue-700">In Progress</span> → In Progress</div>
                    <div><span className="font-medium text-blue-700">On Hold</span> → On Hold</div>
                    <div><span className="font-medium text-blue-700">Resolved</span> → Resolved</div>
                    <div><span className="font-medium text-blue-700">Closed</span> → Closed</div>
                    <div><span className="font-medium text-blue-700">Cancelled</span> → Cancelled</div>
                  </div>
                </div>

                {/* Problem States */}
                <div className="bg-white rounded-lg p-3 border border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-1.5 text-xs">Problem</h4>
                  <div className="space-y-0.5 text-xs">
                    <div><span className="font-medium text-blue-700">New</span> → New</div>
                    <div><span className="font-medium text-blue-700">Assessment</span> → Assess 1, Assess 2, Root Cause Analysis</div>
                    <div><span className="font-medium text-blue-700">Resolved</span> → Resolved</div>
                    <div><span className="font-medium text-blue-700">Closed</span> → Closed</div>
                  </div>
                </div>

                {/* Service Request States */}
                <div className="bg-white rounded-lg p-3 border border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-1.5 text-xs">Service Request</h4>
                  <div className="space-y-0.5 text-xs">
                    <div><span className="font-medium text-blue-700">New</span> → New</div>
                    <div><span className="font-medium text-blue-700">Information Required</span> → Pending Approval</div>
                    <div><span className="font-medium text-blue-700">Approved</span> → Approved</div>
                    <div><span className="font-medium text-blue-700">Closed</span> → Closed</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-2 bg-yellow-50 border border-yellow-300 rounded">
              <p className="text-xs text-yellow-800">
                <strong>Example:</strong> STATE = "In Progress" matches different states per entity type.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlaConditionForm;
